/**
 * useViewportContext — Registers a viewport serializer for the current view.
 *
 * Each page/view calls this hook to tell the AgentContext what data is currently
 * rendered on screen. The agent uses this to answer questions about what the user
 * sees without making tool calls.
 *
 * Usage:
 *   useViewportContext('vendor_detail', () => ({
 *     vendor: { id: vendor.id, name: vendor.name },
 *     technicalFit: { overall_score: 78, items: [...] },
 *   }));
 *
 * The serializer is called lazily when the user sends a message to the agent.
 * It should return a plain JSON-serializable object representing the key data
 * currently visible to the user. Keep it focused — don't dump everything.
 */
import { useEffect, useRef } from 'react';
import { useAgent } from '../contexts/AgentContext';

export type ViewportSerializer = () => Record<string, any>;

/**
 * Register a viewport serializer for the given view name.
 * The serializer will be called when the agent needs the current viewport context.
 *
 * @param viewName - Identifier for this view (e.g. "vendor_detail", "scorecards")
 * @param serializer - Function that returns the current viewport data
 * @param deps - Dependency array — re-registers when deps change (like useEffect)
 */
export function useViewportContext(
  viewName: string,
  serializer: ViewportSerializer,
  deps: any[] = [],
): void {
  const { registerViewport, unregisterViewport } = useAgent();
  const serializerRef = useRef<ViewportSerializer>(serializer);

  // Keep the ref up to date so we always call the latest serializer
  useEffect(() => {
    serializerRef.current = serializer;
  }, [serializer, ...deps]);

  // Register on mount, unregister on unmount
  useEffect(() => {
    const stableSerializer: ViewportSerializer = () => serializerRef.current();
    registerViewport(viewName, stableSerializer);

    return () => {
      unregisterViewport(viewName);
    };
  }, [viewName, registerViewport, unregisterViewport]);
}

export default useViewportContext;
